/**
 * DivergingBarPreview Component
 * Renders diverging bar charts using visx — horizontal bars extending left (negative) and right (positive) from center
 */

import React, { useMemo } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
import { GridColumns } from '@visx/grid';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { useTheme } from '../../contexts/ThemeContext';
import { useData } from '../../contexts/DataContext';
import transformData from '../../utils/transformData';
import { useChartTooltip } from './useChartTooltip';
import AxisLaneDebugOverlay from './AxisLaneDebugOverlay';
import ChartTitle from './ChartTitle';
import ChartLegend from './ChartLegend';
import {
  computeChartLayout,
  computeAxisLanes,
  getAxisSpacingSpec,
  getXAxisBaselineNudge,
  isAxisDebugEnabled,
  reportAxisLaneClamp,
  computeTitleFontSize,
  resolveYAxisLabels,
  LEGEND_SPACING, legendTopSpace, legendTopTranslateY, computeLegendRows,
  formatCompactNumber,
  getCompactTickFormatter,
  computeBarWidth,
} from '../../utils/chartLayout';
import { getPrimaryColorPair, resolveColorByMapping } from '../../charts/shared/palettes';

// Rectangle with left corners rounded (negative bars — left edge at x)
function leftRoundedRect(x, y, w, h, r) {
  const radius = Math.min(r, h / 2, w);
  if (radius <= 0 || w <= 0) return `M${x},${y} L${x + w},${y} L${x + w},${y + h} L${x},${y + h} Z`;
  return [
    `M${x + w},${y}`,
    `L${x + radius},${y}`,
    `Q${x},${y} ${x},${y + radius}`,
    `L${x},${y + h - radius}`,
    `Q${x},${y + h} ${x + radius},${y + h}`,
    `L${x + w},${y + h}`,
    `Z`,
  ].join(' ');
}

// Rectangle with right corners rounded (positive bars extending right from center)
function rightRoundedRect(x, y, w, h, r) {
  const radius = Math.min(r, h / 2, w);
  if (radius <= 0 || w <= 0) return `M${x},${y} L${x + w},${y} L${x + w},${y + h} L${x},${y + h} Z`;
  return [
    `M${x},${y}`,
    `L${x + w - radius},${y}`,
    `Q${x + w},${y} ${x + w},${y + radius}`,
    `L${x + w},${y + h - radius}`,
    `Q${x + w},${y + h} ${x + w - radius},${y + h}`,
    `L${x},${y + h}`,
    `Z`,
  ].join(' ');
}

function DivergingBarPreview({ chartType, config, width, height }) {
  const { resolved: theme } = useTheme();
  const { isLoaded, derivedData: userData, mapping } = useData();
  const sampleConfig = useMemo(
    () => ({ numCategories: config.numCategories }),
    [config.numCategories],
  );

  const rawData = useMemo(() => {
    if (isLoaded && userData.length > 0 && Object.keys(mapping).length > 0) {
      return transformData(userData, mapping, chartType.id);
    }
    if (chartType.getSampleData) return chartType.getSampleData(sampleConfig);
    return chartType.sampleData;
  }, [isLoaded, userData, mapping, chartType, sampleConfig]);

  const data = useMemo(() => {
    const sorted = [...rawData];
    if (config.barOrdering === 'ascending') {
      sorted.sort((a, b) => a.value - b.value);
    } else if (config.barOrdering === 'descending') {
      sorted.sort((a, b) => b.value - a.value);
    }
    return sorted;
  }, [rawData, config.barOrdering]);

  const colorByResult = useMemo(
    () => resolveColorByMapping(data, config, theme),
    [data, config, theme],
  );
  const showLegend = colorByResult ? true : !!config.showLegend;

  const categories = data.map(d => d.name);
  const values = data.map(d => d.value);
  const tickSize = theme.tickSize ?? 13;
  const labelSize = theme.labelSize ?? 15;
  const spacingSpec = getAxisSpacingSpec(theme);
  const { xTickGap, yTickGap, yTickBaselineNudge, xTickLength } = spacingSpec;
  const xTickBaselineNudgeNumeric = getXAxisBaselineNudge({ tickType: 'numeric', spacingSpec });
  const valueLabelSize = theme.valueLabelSize ?? 12;
  const centerVal = config.centerLineValue !== '' && config.centerLineValue != null
    ? parseFloat(config.centerLineValue)
    : 0;

  const colorPair = getPrimaryColorPair(config, theme);
  const posColor = colorPair.positive;
  const negColor = colorPair.negative;

  const legendItems = useMemo(
    () => (
      colorByResult
        ? colorByResult.legendItems
        : [
            { key: 'positive', label: 'Positive', color: posColor },
            { key: 'negative', label: 'Negative', color: negColor },
          ]
    ),
    [colorByResult, posColor, negColor],
  );

  const legendMarkerScale = theme.legendMarkerSize != null ? theme.legendMarkerSize / LEGEND_SPACING.rectSize : 1;
  const estimatedLegendRows = useMemo(() => {
    const { rowCount } = computeLegendRows(legendItems, Math.max(120, width - 160), theme.legendSize ?? 12, legendMarkerScale);
    return rowCount;
  }, [legendItems, width, theme.legendSize, legendMarkerScale]);

  const { margin, innerWidth, innerHeight } = useMemo(() => computeChartLayout({
    width,
    height,
    title: config.title,
    titleFontSize: computeTitleFontSize(config.title, width) || undefined,
    xLabels: categories,
    tickFontSize: theme.tickSize ?? 13,
    tickFontFamily: theme.fontBody,
    hasXLabel: !!config.xlabel,
    hasYLabel: !!config.ylabel,
    orientation: 'horizontal',
    baseRight: config.showValues ? 60 : 30,
    baseBottom: 70,
    horizontalLeftMax: 180,
    legend: {
      show: showLegend,
      position: config.legendPosition || 'top',
      topSpace: legendTopSpace(estimatedLegendRows),
      bottomSpace: 80 + (estimatedLegendRows - 1) * LEGEND_SPACING.rowHeight,
      rightSpace: 150,
    },
  }), [width, height, config.title, showLegend, config.legendPosition, config.showValues, config.xlabel, config.ylabel, categories, theme.tickSize, theme.fontBody, estimatedLegendRows]);
  const yLanes = useMemo(() => computeAxisLanes({
    axisSide: 'left',
    labels: categories,
    tickFontSize: tickSize,
    tickFontFamily: theme.fontBody,
    titleFontSize: labelSize,
    showTicks: false,
    tickLength: 0,
    availableMargin: margin.left,
    spacingSpec: { ...getAxisSpacingSpec(theme), gapY: yTickGap },
    telemetryMeta: { chartId: 'diverging-bar', viewportWidth: width, viewportHeight: height },
  }), [margin.left, categories, tickSize, labelSize, yTickGap, theme, width, height]);

  const xExtent = useMemo(() => {
    const allVals = [...values, centerVal];
    const minV = Math.min(...allVals);
    const maxV = Math.max(...allVals);
    const configMin = config.xMin && config.xMin.trim() !== '' ? parseFloat(config.xMin) : null;
    const configMax = config.xMax && config.xMax.trim() !== '' ? parseFloat(config.xMax) : null;
    const pad = Math.max((maxV - minV) * 0.1, 1);
    return [
      configMin ?? Math.min(minV - pad, centerVal),
      configMax ?? Math.max(maxV + pad, centerVal),
    ];
  }, [values, centerVal, config.xMin, config.xMax]);

  const yScale = useMemo(
    () => scaleBand({ domain: categories, range: [0, innerHeight], padding: 0.35 }),
    [categories, innerHeight],
  );

  const xScale = useMemo(
    () => scaleLinear({ domain: xExtent, range: [0, innerWidth], nice: true }),
    [xExtent, innerWidth],
  );
  const xTicksForLayout = useMemo(
    () => scaleLinear({ domain: xExtent, range: [0, 100], nice: true }).ticks(5).map((v) => String(v)),
    [xExtent],
  );

  const barHeightRatio = config.barHeight ?? 0.6;
  const bw = computeBarWidth(yScale.bandwidth() * (barHeightRatio / 0.6), innerHeight, data.length, { maxFraction: 0.085 });
  const bOff = (yScale.bandwidth() - bw) / 2;
  const effectiveRadius = Math.min(theme.barRadius ?? 4, bw * 0.15);

  const yAxisResolved = useMemo(() => resolveYAxisLabels({
    labels: categories,
    innerHeight,
    baseFontSize: tickSize,
    fontFamily: theme.fontBody,
  }), [categories, innerHeight, tickSize, theme.fontBody]);

  const decimals = config.valueDecimals ?? 1;
  const centerX = xScale(centerVal);
  const centerStyle = config.centerLineStyle || 'solid';
  const xLanes = computeAxisLanes({
    axisSide: 'bottom',
    labels: xTicksForLayout,
    tickFontSize: tickSize,
    tickFontFamily: theme.fontBody,
    titleFontSize: labelSize,
    showTicks: true,
    tickLength: xTickLength,
    spacingSpec: { ...getAxisSpacingSpec(theme), gapX: xTickGap },
    telemetryMeta: { chartId: 'diverging-bar', viewportWidth: width, viewportHeight: height },
  });
  const xAxisTitleY = innerHeight + xLanes.titleOffsetPx;
  const axisDebugEnabled = isAxisDebugEnabled();
  if (axisDebugEnabled) {
    reportAxisLaneClamp(yLanes);
    reportAxisLaneClamp(xLanes);
  }

  const axisTickProps = () => ({ fill: theme.tickColor || theme.foreground, fontSize: tickSize });

  const { containerRef, showTip, hideTooltip, Tip } = useChartTooltip(theme);

  return (
    <div ref={containerRef} style={{ width, height, position: 'relative', backgroundColor: theme.background }}>
      <ChartTitle title={config.title} chartWidth={width} marginLeft={margin.left} marginRight={margin.right} theme={theme} />

      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }} overflow="visible" onMouseLeave={hideTooltip}>
        <Group left={margin.left} top={margin.top}>
          {/* Legend – Top */}
          {showLegend && config.legendPosition === 'top' && (
            <ChartLegend items={legendItems} innerWidth={innerWidth} theme={theme} position="top" transform={`translate(0, ${legendTopTranslateY(estimatedLegendRows, width, height)})`} />
          )}

          {config.showGrid && (
            <GridColumns
              scale={xScale}
              height={innerHeight}
              strokeDasharray="3 3"
              stroke={theme.gridColor}
              strokeOpacity={theme.gridAlpha}
            />
          )}

          {data.map((d, i) => {
            const y = (yScale(d.name) ?? 0) + bOff;
            const val = d.value;
            const isPositive = val >= centerVal;
            const centerXPos = xScale(centerVal);
            const valueXPos = xScale(val);
            const barWidth = Math.abs(valueXPos - centerXPos);

            if (barWidth < 0.5) return null;

            const barColor = colorByResult ? colorByResult.fillFn(d) : (isPositive ? posColor : negColor);
            const pathD = isPositive
              ? rightRoundedRect(centerXPos, y, valueXPos - centerXPos, bw, effectiveRadius)
              : leftRoundedRect(valueXPos, y, centerXPos - valueXPos, bw, effectiveRadius);

            const labelX = isPositive ? valueXPos + 5 : valueXPos - 5;
            const labelAnchor = isPositive ? 'start' : 'end';
            const fmt = (v) => (v >= centerVal ? `+${formatCompactNumber(v, decimals)}` : formatCompactNumber(v, decimals));

            return (
              <g
                key={d.name}
                onMouseMove={(e) => showTip(e, fmt(val))}
                style={{ cursor: 'pointer' }}
              >
                <path d={pathD} fill={barColor} />
                {config.showValues && (
                  <text
                    x={labelX}
                    y={y + bw / 2}
                    dominantBaseline="middle"
                    textAnchor={labelAnchor}
                    fontSize={valueLabelSize}
                    fontWeight={500}
                    fill={theme.foreground}
                  >
                    {fmt(val)}
                  </text>
                )}
              </g>
            );
          })}

          {centerStyle !== 'none' && (
            <line
              x1={centerX}
              y1={0}
              x2={centerX}
              y2={innerHeight}
              stroke={theme.axisColor}
              strokeWidth={theme.spineWidth ?? 1}
              strokeDasharray={centerStyle === 'dashed' ? '4 4' : undefined}
            />
          )}

          <AxisLeft
            scale={yScale}
            tickValues={yAxisResolved.tickValues}
            hideAxisLine
            hideTicks
            stroke={theme.axisColor}
            strokeWidth={theme.spineWidth}
            tickStroke={theme.axisColor}
            tickFormat={yAxisResolved.tickFormat}
            tickLabelProps={() => ({
              ...axisTickProps(),
              fontSize: yAxisResolved.fontSize,
              textAnchor: 'end',
              dx: -yTickGap,
              dy: yTickBaselineNudge,
            })}
          />
          <AxisBottom
            scale={xScale}
            top={innerHeight}
            stroke={theme.axisColor}
            strokeWidth={theme.spineWidth}
            tickStroke={theme.axisColor}
            tickLength={xTickLength}
            tickFormat={getCompactTickFormatter(Math.max(Math.abs(xScale.domain()[0]), Math.abs(xScale.domain()[1])))}
            tickLabelProps={() => ({ ...axisTickProps(), textAnchor: 'middle', dy: xTickBaselineNudgeNumeric })}
          />
          <text
            x={-innerHeight / 2}
            y={-yLanes.titleOffsetPx}
            transform="rotate(-90)"
            textAnchor="middle"
            fontSize={labelSize}
            fill={theme.labelColor}
          >
            {config.ylabel || ''}
          </text>
          <text
            x={innerWidth / 2}
            y={xAxisTitleY}
            textAnchor="middle"
            fontSize={labelSize}
            fill={theme.labelColor}
          >
            {config.xlabel || ''}
          </text>
          {axisDebugEnabled && (
            <AxisLaneDebugOverlay
              lanes={[
                {
                  axisSide: 'left',
                  tickLabelOffsetPx: yLanes.tickLabelOffsetPx,
                  titleOffsetPx: yLanes.titleOffsetPx,
                  clamped: yLanes.clamped,
                  spineX: 0,
                  spineY1: 0,
                  spineY2: innerHeight,
                },
                {
                  axisSide: 'bottom',
                  tickLabelOffsetPx: xLanes.tickLabelOffsetPx,
                  titleOffsetPx: xLanes.titleOffsetPx,
                  clamped: xLanes.clamped,
                  spineY: innerHeight,
                  spineX1: 0,
                  spineX2: innerWidth,
                },
              ]}
            />
          )}

          {/* Legend – Bottom */}
          {showLegend && config.legendPosition === 'bottom' && (
            <ChartLegend items={legendItems} innerWidth={innerWidth} theme={theme} position="bottom" transform={`translate(0, ${innerHeight + LEGEND_SPACING.bottomGap})`} />
          )}
        </Group>

        {/* Legend – Right (outside the plot group) */}
        {showLegend && config.legendPosition === 'right' && (
          <ChartLegend
            items={legendItems}
            innerWidth={innerWidth}
            theme={theme}
            position="right"
            transform={`translate(${margin.left + innerWidth + LEGEND_SPACING.rightGap}, ${margin.top + innerHeight / 2 - 12})`}
          />
        )}
      </svg>
      <Tip />
    </div>
  );
}

export default DivergingBarPreview;
